import { shallowEqual, useSelector } from 'react-redux';
import { ILenderVehicle, IRACVehicle, IUser, IVehicle, TFetchingVehicleFlagsSelector } from 'modules/core/types';
import { VehicleTablePlaceholderType } from 'modules/core/enums';
import { useDebounceVariable } from 'modules/core/hooks';
import { IS_FETCHING_VEHICLE_DELAY } from 'modules/core/constants';
import { useEffect, useState } from 'react';
import { getUser } from 'store/selectors/auth.selector';

export const useTablePlaceholderType = (
  isValid: boolean,
  vehicles: IVehicle[] | IRACVehicle[] | ILenderVehicle[],
  fetchingVehicleFlagsSelector: TFetchingVehicleFlagsSelector,
  isValidStock?: boolean,
  isRequestPending?: boolean,
): VehicleTablePlaceholderType => {
  const { isFetchingVehicles, isFetchingVehiclesFailed, resetCount } = useSelector(
    fetchingVehicleFlagsSelector,
    shallowEqual,
  );
  const user = useSelector(getUser) as IUser;

  const [isWasRequest, setIsWasRequest] = useState(false);

  useEffect(() => {
    if (isFetchingVehicles) setIsWasRequest(true);

    if (!!resetCount) setIsWasRequest(false);
  }, [isFetchingVehicles, resetCount]);

  // useDebounceVariable hook is used to prevent error placeholder from being displayed for short period of time when request is canceled.
  const debouncedIsFetchingVehiclesFailed = useDebounceVariable(
    !!resetCount ? false : isFetchingVehiclesFailed,
    IS_FETCHING_VEHICLE_DELAY,
  );

  const isNoVehiclesFound = isValid && !vehicles.length;

  if (!isValidStock) return VehicleTablePlaceholderType.NEED_STOCK;

  if (debouncedIsFetchingVehiclesFailed && !resetCount) return VehicleTablePlaceholderType.ERROR;

  if (user?.is_sso && !user?.isWithInventory && !isFetchingVehicles && isNoVehiclesFound && !isWasRequest)
    return VehicleTablePlaceholderType.DEFAULT;

  if (isFetchingVehicles) return VehicleTablePlaceholderType.LOADING;

  if (isFetchingVehiclesFailed && !debouncedIsFetchingVehiclesFailed && !resetCount)
    return VehicleTablePlaceholderType.LOADING;

  if (!!resetCount && !isWasRequest) return VehicleTablePlaceholderType.DEFAULT;

  if (isRequestPending && isNoVehiclesFound && isWasRequest) return VehicleTablePlaceholderType.LOADING;

  if (isNoVehiclesFound && !isFetchingVehicles && isWasRequest) return VehicleTablePlaceholderType.SEARCH;

  return VehicleTablePlaceholderType.DEFAULT;
};
